import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Box, Flex } from "reflexbox/styled-components";
import { useApplicationStateValue } from "../../application/Application";
import { Carousel } from "antd";
import leftChevronSvg from "../../assets/images/left-chevron.svg";
import rightChevronSvg from "../../assets/images/right-chevron.svg";
import { withLocalizeStrings } from "../../languages/Localize";
import { getActualPanel, getFormattedNamesFromArray, getFormattedTime } from "../../util/util";
import starFullSvg from "../../assets/images/star-full-blue.svg";
import { useFavoriteStateValue } from "../../favorite/Favorite";
import RenderIf from "../../common/conditions/RenderIf";
import { useHistory } from "react-router";
import routes from "../../config/routes";

const StyledContainer = styled(Box)`
    display: grid;
    grid-template-rows: 25% 75%;
    height: 100%;
    & .ant-carousel {
        height: 100%;
        overflow: hidden;
    }
`;

const StyledHeaderContainer = styled(Flex).attrs(() => ({
    paddingLeft: [7, 8],
    paddingRight: [6, 7],
    height: [26,28]
}))``;

const StyledLabel = styled(Box).attrs(() => ({ fontSize: [3, 4, 4], fontWeight: "normal" }))`
    flex: 8;
    color: ${({ theme }) => theme.colors.white};
`;

const StyledCounterContainer = styled(Flex)`
    flex: 1;
    align-self: end;
`;

const StyledCounter = styled(Box).attrs(() => ({ fontSize: [3, 4, 4], fontWeight: "bold" }))`
    background: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.primary};
    border-radius: 2px;
    height: 70%;
    text-align: center;
    width: 80%;
`;

const StyledCarousel = styled(Carousel)`
    & .slick-list,
    & .slick-track,
    &.slick-slider {
        height: 100%;
    }
    & .slick-slide > div {
        height: 80%;
        width: 80%;
        margin: 0 auto;
    }
    &.slick-slider .slick-arrow {
        height: 80%;
        width: 10%;
        top: 0;
        margin: 0;
        z-index: 10;
    }
    &.slick-slider .slick-prev {
        left: -2%;
        background: url(${leftChevronSvg}) no-repeat center;
        background-size: contain;
    }
    &.slick-slider .slick-next {
        right: -2%;
        background: url(${rightChevronSvg}) no-repeat center;
        background-size: contain;
    }

    && .slick-dots.slick-dots-bottom {
        bottom: 0;
        margin-bottom: 8px;
        align-items: center;
    }

    && .slick-dots.slick-dots-bottom li {
        height: 4px;
        background: ${({ theme }) => theme.colors.primary};
        vertical-align: middle;
        box-sizing: border-box;
        width: 20px;
    }
    && .slick-dots.slick-dots-bottom li.slick-active {
        height: 8px;
        vertical-align: middle;
        width: 25px;
        background: ${({ theme }) => theme.colors.white};
    }

    && .slick-dots.slick-dots-bottom button {
        height: 100%;
        width: 100%;

        background: inherit;
    }
`;

const StyledContentContainer = styled(Box)`
    width: 100%;
    position: relative;
    background: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.primary};
    text-align: center;
    border-radius: 5px;
    height: 100%;
`;

const FavoriteFlag = styled(Box)`
    background: url(${starFullSvg}) no-repeat center;
    background-size: contain;
    height: 100%;
    width: 30px;
    position: absolute;
    right: 10%;
    top: 0;
`;

const StyledContent = styled(Flex).attrs(() => ({ padding: [1, 2] }))`
    width: 70%;
    flex-direction: column;
    height: 100%;
`;

const StyledPanelContainer = styled(Flex)`
    width: 100%;
    height: 70%;
    flex-direction: column;
`;

const StyledPanelLabel = styled(Box).attrs(() => ({ fontSize: [1, 2, 2], fontWeight: "normal" }))`
    width: 100%;
    flex-wrap: wrap;
    text-align: left;
    color: ${({ theme }) => theme.colors.primary};
    line-height: ${({ theme }) => theme.fontSizes[2]}px;
`;

const StyledModerator = styled(Box).attrs(() => ({ fontSize: [0, 1, 1], color: "gray", fontWeight: "bold" }))`
    width: 100%;
    text-align: left;
    flex-wrap: wrap;
    color: #1f1f1f;
`;

const StyledSpan = styled.span.attrs(() => ({ fontSize: [0, 1, 1] }))`
    font-weight: ${({ theme }) => theme.fontWeights.normal};
    
`;

const StyledItemFooter = styled(Flex).attrs(() => ({ fontSize: [0, 1, 1], fontWeight: "bold" }))`
    width: 100%;
    height: 30%;
    justify-content: flex-start;
    text-transform: uppercase;
    align-items: flex-end;
`;

const NoDataStyledContainer = styled(Box)`
    background: ${({ theme }) => theme.colors.transparent};
    height: 100%;
`;
const NoDataStyledHeaderLabel = styled(Flex)`
    color: ${({ theme }) => theme.colors.white};
    text-align: center;
    justify-content: center;
    align-items: center;
    height: 100%;
    padding: ${({ theme }) => theme.space[3]}px;
`;

const ActualPanels = ({ strings }) => {
    const history = useHistory();
    const {
        appData: { forumPanels, forumSpeakers },
        forumFullAgenda
    } = useApplicationStateValue();
    const { favoritePanels } = useFavoriteStateValue();
    const [data, setData] = useState(null);
    const [len, setLen] = useState(0);

    const onClick = id => {
        history.push(`${routes.PANELS_DETAILS}${id}`);
    };

    useEffect(() => {
        let actualData = getActualPanel(forumFullAgenda);
        setData(actualData);
        setLen(!actualData.panels ? 0 : actualData.panels.length);
        const interval = setInterval(() => {
            actualData = getActualPanel(forumFullAgenda);
            setData(actualData);
            setLen(!actualData.panels ? 0 : actualData.panels.length);
        }, 6000);
        return () => (interval !== null ? clearInterval(interval) : null);
    }, [forumFullAgenda]);

    const afterChangeHandler = current => {};

    const actualPanels =
        data === null || Object.keys(data).length === 0 || data.panels.length === 0
            ? null
            : data.panels.map(id => {
                  if (!!forumPanels[id]) {
                      const moderator = ` ${getFormattedNamesFromArray(forumPanels[id].moderators, forumSpeakers, "name")}`;
                      const footer = `${strings.panel.END} ${getFormattedTime(forumPanels[id].endTime)}, `;

                      return (
                          <StyledContentContainer key={id} onClick={onClick.bind(null, id)}>
                              <StyledContent>
                                  <StyledPanelContainer>
                                      <StyledPanelLabel><b>{forumPanels[id].label}</b></StyledPanelLabel>
                                      <StyledModerator>
                                          <StyledSpan>{strings.panel.MODERATOR}</StyledSpan>
                                          {moderator}
                                      </StyledModerator>
                                  </StyledPanelContainer>
                                  <StyledItemFooter fontSize={[0, 1]}>
                                      {footer}
                                      <StyledSpan>{forumPanels[id].room}</StyledSpan>
                                  </StyledItemFooter>
                              </StyledContent>
                              <RenderIf render={() => <FavoriteFlag />} if={favoritePanels.includes(id)} />
                          </StyledContentContainer>
                      );
                  } else {
                      return null;
                  }
              });

    return (

        <StyledContainer>
            <StyledHeaderContainer>
                <StyledLabel>{strings.home.ACTUAL_PANELS_HEADER}</StyledLabel>
                <StyledCounterContainer>
                    <StyledCounter>{len}</StyledCounter>
                </StyledCounterContainer>
            </StyledHeaderContainer>
            <StyledCarousel arrows afterChange={afterChangeHandler}>
                {actualPanels || (
                    <NoDataStyledContainer>
                        <NoDataStyledHeaderLabel fontSize={[3, 4]} padding={3}>
                            {strings.panel.NO_ACTUAL_PANELS}
                        </NoDataStyledHeaderLabel>
                    </NoDataStyledContainer>
                )}
            </StyledCarousel>
        </StyledContainer>
    );
};

export default withLocalizeStrings(ActualPanels);
